import { Loader2, Inbox, AlertTriangle, Lock, Clock, WifiOff, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { AtmosphereLayer } from "@/components/appearance/AtmosphereLayer";

// Reusable, warmly-worded status states used across ContentOS.
function Frame({ icon: Icon, title, message, children, testid, tone = "muted" }) {
  const ring = tone === "danger" ? "text-destructive bg-destructive/10"
    : tone === "lock" ? "text-accent bg-accent/10" : "text-muted-foreground bg-muted";
  return (
    <div className="flex flex-col items-center justify-center px-6 py-16 text-center" data-testid={testid}>
      <span className={cn("mb-4 flex h-14 w-14 items-center justify-center rounded-2xl", ring)}>
        <Icon className="h-7 w-7" strokeWidth={1.75} />
      </span>
      <h3 className="font-heading text-lg font-semibold text-foreground">{title}</h3>
      {message && <p className="mt-1.5 max-w-sm text-sm text-muted-foreground">{message}</p>}
      {children && <div className="mt-5">{children}</div>}
    </div>
  );
}

export function LoadingState({ label = "Just a moment…", testid = "state-loading" }) {
  return (
    <div className="flex items-center justify-center gap-2 py-16 text-muted-foreground" data-testid={testid}>
      <Loader2 className="h-5 w-5 animate-spin text-accent" />
      <span className="text-sm">{label}</span>
    </div>
  );
}

export function EmptyState({ title = "Nothing here yet", message = "This space will fill up as you and your team get going.", action, testid = "state-empty" }) {
  return (
    <div className="relative overflow-hidden">
      <AtmosphereLayer surface="empty_state" slot="empty_state" />
      <div className="relative z-10"><Frame icon={Inbox} title={title} message={message} testid={testid}>{action}</Frame></div>
    </div>
  );
}

export function ErrorState({ title = "Something went sideways", message = "We couldn’t load this just now. Please try again.", onRetry, testid = "state-error" }) {
  return (
    <Frame icon={AlertTriangle} title={title} message={message} testid={testid} tone="danger">
      {onRetry && <Button variant="outline" onClick={onRetry} data-testid="state-error-retry"><RefreshCw className="mr-2 h-4 w-4" /> Try again</Button>}
    </Frame>
  );
}

export function PermissionDenied({ message = "You don’t have access to this area. If you think you should, please ask an administrator.", testid = "state-permission-denied" }) {
  return <Frame icon={Lock} title="This area is restricted" message={message} testid={testid} tone="lock" />;
}

export function NotAvailableYet({ title = "Coming soon", message = "We’re still building this part of ContentOS. It’ll be ready in an upcoming release.", testid = "state-not-available" }) {
  return <Frame icon={Clock} title={title} message={message} testid={testid} tone="lock" />;
}

export function ConnectionProblem({ onRetry, testid = "state-connection" }) {
  return (
    <Frame icon={WifiOff} title="We’re having trouble connecting" message="Please check your connection and try again." testid={testid} tone="danger">
      {onRetry && <Button variant="outline" onClick={onRetry} data-testid="state-connection-retry"><RefreshCw className="mr-2 h-4 w-4" /> Retry</Button>}
    </Frame>
  );
}
